/**
 * useAvatarPack — polls GET /api/v1/avatar-packs/:id until the pack reaches a
 * terminal status (COMPLETED or FAILED). Returns frames as fully-qualified URLs.
 *
 * The miniapp uses this after a successful purchase to know when to flip the
 * AvatarFlow from "loading" to "reveal" with real generated frames.
 */

import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export type AvatarPackStatus = 'PENDING' | 'GENERATING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export type AvatarPackPhase = 'queued' | 'composing' | 'painting' | 'slicing' | 'finalizing';

export interface AvatarPackFrame {
  id: string;
  imageUrl: string;
  thumbnailUrl: string;
}

export interface AvatarPackSnapshot {
  id: string;
  status: AvatarPackStatus;
  type: string;
  ascendant: string;
  error: string | null;
  frames: AvatarPackFrame[];
  /** Coarse-grained worker phase (null before any phase reported or after terminal). */
  phase: AvatarPackPhase | null;
  /** ISO timestamp when the current phase was last reported. */
  phaseAt: string | null;
  createdAt: string;
  deliveredAt: string | null;
}

interface UseAvatarPackResult {
  snapshot: AvatarPackSnapshot | null;
  error: Error | null;
}

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

export function useAvatarPack(packId: string | null | undefined): UseAvatarPackResult {
  const jwt = useAuth((s) => s.jwt);
  const [snapshot, setSnapshot] = useState<AvatarPackSnapshot | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    setSnapshot(null);
    setError(null);
    if (!packId || !jwt) return;

    const startedAt = Date.now();
    let timer: number | undefined;

    const tick = async (): Promise<void> => {
      if (cancelledRef.current) return;
      try {
        const res = await apiFetch(`/api/v1/avatar-packs/${packId}`, jwt, { method: 'GET' });
        if (!res.ok) {
          throw new Error(`pack_fetch_${res.status}`);
        }
        const data = (await res.json()) as AvatarPackSnapshot;
        if (cancelledRef.current) return;
        setSnapshot(data);
        if (data.status === 'COMPLETED' || data.status === 'FAILED') return;
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          throw new Error('pack_poll_timeout');
        }
        timer = window.setTimeout(() => void tick(), POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelledRef.current) return;
        setError(err as Error);
      }
    };

    void tick();
    return () => {
      cancelledRef.current = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [packId, jwt]);

  return { snapshot, error };
}
